import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const QuoteModal = ({ quote, onClose, onSave }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    quote_number: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    project_type: 'zonnepanelen',
    quote_amount: '',
    status: 'concept',
    quote_date: new Date()?.toISOString()?.split('T')?.[0],
    valid_until: '',
    description: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (quote) {
      setFormData({
        quote_number: quote?.quote_number || '',
        customer_name: quote?.customer_name || '',
        customer_email: quote?.customer_email || '',
        customer_phone: quote?.customer_phone || '',
        project_type: quote?.project_type || 'zonnepanelen',
        quote_amount: quote?.quote_amount || '',
        status: quote?.status || 'concept',
        quote_date: quote?.quote_date || new Date()?.toISOString()?.split('T')?.[0],
        valid_until: quote?.valid_until || '',
        description: quote?.description || '',
        notes: quote?.notes || '',
      });
    }
  }, [quote]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!formData?.customer_name || !formData?.customer_email || !formData?.quote_amount) {
      setError('Vul alle verplichte velden in');
      return;
    }

    try {
      setLoading(true);

      const quoteData = {
        ...formData,
        provider_id: user?.id,
        quote_amount: parseFloat(formData?.quote_amount),
      };

      if (quote?.id) {
        // Update existing quote
        const { error: updateError } = await supabase?.from('provider_quotes')?.update(quoteData)?.eq('id', quote?.id)?.eq('provider_id', user?.id);

        if (updateError) throw updateError;
      } else {
        // Create new quote
        const { error: insertError } = await supabase?.from('provider_quotes')?.insert(quoteData);

        if (insertError) throw insertError;
      }

      onSave();
    } catch (err) {
      console.error('Error saving quote:', err);
      setError(err?.message || 'Er is een fout opgetreden');
    } finally {
      setLoading(false);
    }
  };

  const projectTypeOptions = [
    { value: 'zonnepanelen', label: 'Zonnepanelen' },
    { value: 'isolatie', label: 'Isolatie' },
    { value: 'warmtepomp', label: 'Warmtepomp' },
    { value: 'ventilatie', label: 'Ventilatie' },
    { value: 'thuisbatterij', label: 'Thuisbatterij' },
    { value: 'waterontharder', label: 'Waterontharder' },
    { value: 'overig', label: 'Overig' },
  ];

  const statusOptions = [
    { value: 'concept', label: 'Concept' },
    { value: 'verstuurd', label: 'Verstuurd' },
    { value: 'in_overweging', label: 'In Overweging' },
    { value: 'gewonnen', label: 'Gewonnen' },
    { value: 'verloren', label: 'Verloren' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-heading font-bold text-foreground">
            {quote ? 'Offerte Bewerken' : 'Nieuwe Offerte'}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-error/10 border border-error/20 text-error px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Offertenummer"
              required
              value={formData?.quote_number}
              onChange={(e) => handleChange('quote_number', e?.target?.value)}
              placeholder="OFF-2026-001"
            />
            <Select
              label="Status"
              options={statusOptions}
              value={formData?.status}
              onChange={(value) => handleChange('status', value)}
            />
          </div>

          <Input
            label="Klantnaam"
            required
            value={formData?.customer_name}
            onChange={(e) => handleChange('customer_name', e?.target?.value)}
            placeholder="Jan de Vries"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Email"
              type="email"
              required
              value={formData?.customer_email}
              onChange={(e) => handleChange('customer_email', e?.target?.value)}
              placeholder="jan@example.com"
            />
            <Input
              label="Telefoon"
              value={formData?.customer_phone}
              onChange={(e) => handleChange('customer_phone', e?.target?.value)}
              placeholder="06-12345678"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Type Verduurzaming"
              options={projectTypeOptions}
              value={formData?.project_type}
              onChange={(value) => handleChange('project_type', value)}
            />
            <Input
              label="Offertebedrag (€)"
              type="number"
              required
              value={formData?.quote_amount}
              onChange={(e) => handleChange('quote_amount', e?.target?.value)}
              placeholder="15000"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Offerte Datum"
              type="date"
              required
              value={formData?.quote_date}
              onChange={(e) => handleChange('quote_date', e?.target?.value)}
            />
            <Input
              label="Geldig Tot"
              type="date"
              value={formData?.valid_until}
              onChange={(e) => handleChange('valid_until', e?.target?.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Beschrijving</label>
            <textarea
              className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              rows="3"
              value={formData?.description}
              onChange={(e) => handleChange('description', e?.target?.value)}
              placeholder="Beschrijving van het project..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Notities</label>
            <textarea
              className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              rows="3"
              value={formData?.notes}
              onChange={(e) => handleChange('notes', e?.target?.value)}
              placeholder="Interne notities..."
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuleren
            </Button>
            <Button type="submit" loading={loading}>
              {quote ? 'Opslaan' : 'Offerte Aanmaken'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuoteModal;